import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../Context/UserContext';
import { API_BASE_URL } from '../config';
import { useNavigate, Link } from 'react-router-dom';
import '../Styles/Orders.css';
import '../Styles/Shop.css';

// eslint-disable-next-line no-unused-vars
const getColorHex = (colorName) => {
    if (!colorName) return 'transparent';
    const normalized = colorName.toString().toLowerCase().trim();
    const map = {
        'navy blue': '#000080',
        'burgundy': '#800020',
        'khaki': '#F0E68C',
        'baby blue': '#89CFF0',
        'dusty pink': '#DCAE96',
        'white': '#ffffff',
        'black': '#000000',
        'red': '#ff0000',
        'green': '#008000',
        'blue': '#0000ff',
        'grey': '#808080',
        'gray': '#808080',
        'yellow': '#ffff00',
        'orange': '#ffa500',
        'purple': '#800080',
        'pink': '#ffc0cb',
        'brown': '#a52a2a',
        'beige': '#f5f5dc',
        'silver': '#c0c0c0',
        'gold': '#ffd700'
    };

    if (map[normalized]) return map[normalized];

    const spaceless = normalized.replace(/\s+/g, '');
    if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('color', spaceless)) {
        return spaceless;
    }
    return '#ccc';
};

const Orders = () => {
    const { user, token, logout, loading: userLoading } = useUser();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [orderToEdit, setOrderToEdit] = useState(null);
    const [editItems, setEditItems] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [availableProducts, setAvailableProducts] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [showAddSection, setShowAddSection] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [orderToExchange, setOrderToExchange] = useState(null);
    const [exchangeItems, setExchangeItems] = useState([]);
    const [exchangeNotes, setExchangeNotes] = useState('');
    const [isExchanging, setIsExchanging] = useState(false);
    const [quickViewProduct, setQuickViewProduct] = useState(null);
    const [quickViewVariations, setQuickViewVariations] = useState([]);
    const [qvSelectedColor, setQvSelectedColor] = useState(null);
    const [qvSelectedSize, setQvSelectedSize] = useState(null);
    const [quickViewIndex, setQuickViewIndex] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [exchangeSuccessMessage, setExchangeSuccessMessage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const storedOrderId = sessionStorage.getItem('editingOrderId');
        const storedItems = sessionStorage.getItem('editingOrderItems');

        if (storedOrderId && storedItems && orders.length > 0 && !orderToEdit) {
            const order = orders.find(o => o.id.toString() === storedOrderId);
            if (order) {
                setOrderToEdit(order);
                let parsedItems = JSON.parse(storedItems);

                const productIds = [...new Set(parsedItems.map(i => i.product_id))];
                productIds.forEach(productId => {
                    const groupItems = parsedItems.filter(i => i.product_id === productId);
                    const totalQty = groupItems.reduce((sum, i) => sum + Number(i.quantity), 0);

                    groupItems.forEach(gItem => {
                        const offQty = Number(gItem.offer_qty) || 0;
                        const offPrice = parseFloat(gItem.offer_price) || 0;
                        let unitPrice = parseFloat(gItem.price) || 0;
                        let unitDiscount = 0;

                        if (offQty > 0 && offPrice > 0 && totalQty >= offQty) {
                            const effectiveUnitPrice = offPrice / offQty;
                            unitDiscount = unitPrice - effectiveUnitPrice;
                        } else {
                            if (gItem.discount_type === 'percentage') {
                                unitDiscount = unitPrice * (parseFloat(gItem.discount_value) / 100);
                            } else if (gItem.discount_type === 'fixed') {
                                unitDiscount = parseFloat(gItem.discount_value || 0);
                            }
                        }

                        gItem.discount = unitDiscount * gItem.quantity;
                        gItem.total = (unitPrice * gItem.quantity) - gItem.discount;
                    });
                });

                setEditItems(parsedItems);
            }
        }
    }, [orders, orderToEdit]);

    const fetchOrders = useCallback(async () => {
        if (!user || !token) return;
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/orders?customer_id=${user.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.error || data.message || 'Failed to fetch orders');
            }

            setOrders(data);
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
            // If unauthorized, redirect to login
            if (err.message.includes('Token') || err.message.includes('Unauthorized') || err.message.includes('expired')) {
                setTimeout(() => {
                    logout();
                    navigate('/auth');
                }, 2000);
            }
        } finally {
            setLoading(false);
        }
    }, [user, token, logout, navigate]);

    useEffect(() => {
        if (!userLoading && !user) {
            navigate('/auth');
            return;
        }
        if (user) fetchOrders();
    }, [user, userLoading, navigate, fetchOrders]);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getStatusClass = (status) => {
        const s = status?.toLowerCase();
        if (s === 'pending') return 'status-pending';
        if (s === 'completed' || s === 'delivered') return 'status-completed';
        if (s === 'cancelled' || s === 'rejected') return 'status-cancelled';
        if (s === 'processing' || s === 'shipped') return 'status-processing';
        return '';
    };

    const getFilteredOrders = () => {
        return orders.filter(order => {
            let matchesDate = true;
            let matchesStatus = true;
            if (fromDate || toDate) {
                const orderDate = new Date(order.created_at).toISOString().split('T')[0];
                if (fromDate && toDate) matchesDate = orderDate >= fromDate && orderDate <= toDate;
                else if (fromDate) matchesDate = orderDate >= fromDate;
                else if (toDate) matchesDate = orderDate <= toDate;
            }
            if (statusFilter) matchesStatus = order.status?.toLowerCase() === statusFilter.toLowerCase();
            return matchesDate && matchesStatus;
        });
    };

    const handleEditOrder = async (order) => {
        if (order.status?.toLowerCase() !== 'pending') {
            alert('Only pending orders can be edited.');
            return;
        }
        setOrderToEdit(order);

        // Restore the original unit price for each item, in case it was saved as the discounted price
        const fixedItems = order.items.map(item => ({
            ...item,
            price: ((parseFloat(item.total) + parseFloat(item.discount || 0)) / Number(item.quantity)).toFixed(2)
        }));

        setEditItems(fixedItems);
        setShowAddSection(false);

        // Fetch products for "Add Item" feature
        try {
            const response = await fetch(`${API_BASE_URL}/admin/products`);
            if (response.ok) {
                const data = await response.json();
                setAvailableProducts(data);
            }
        } catch (err) {
            console.error('Failed to fetch products:', err);
        }
    };

    const calculateTotals = (items) => {
        let subtotal = 0;

        const productGroups = {};
        items.forEach(item => {
            if (!productGroups[item.product_id]) productGroups[item.product_id] = [];
            productGroups[item.product_id].push(item);
        });

        for (const productId in productGroups) {
            const group = productGroups[productId];
            const totalQty = group.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
            const firstItem = group[0];
            const offQty = Number(firstItem.offer_qty) || 0;
            const offPrice = parseFloat(firstItem.offer_price) || 0;

            if (offQty > 0 && offPrice > 0 && totalQty >= offQty) {
                const effectiveUnitPrice = offPrice / offQty;
                subtotal += effectiveUnitPrice * totalQty;
            } else {
                for (const item of group) {
                    const qty = Number(item.quantity) || 0;
                    const price = parseFloat(item.price) || 0;

                    let disc = 0;
                    if (item.discount_type === 'percentage') {
                        disc = price * (parseFloat(item.discount_value) / 100);
                    } else if (item.discount_type === 'fixed') {
                        disc = parseFloat(item.discount_value || 0);
                    }
                    const actualUnitPrice = price - disc;
                    subtotal += actualUnitPrice * qty;
                }
            }
        }

        const totalAfterItemDiscounts = subtotal;

        const hasItemBenefits = items.some(item => {
            const groupItems = items.filter(i => i.product_id === item.product_id);
            const totalQty = groupItems.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
            const offQty = Number(item.offer_qty) || 0;
            const hasAppliedOffer = offQty > 0 && totalQty >= offQty;
            const hasItemDiscount = parseFloat(item.discount_value) > 0;
            return hasAppliedOffer || hasItemDiscount;
        });

        let orderDiscountAmount = 0;
        if (!hasItemBenefits) {
            if (orderToEdit.discount_type === 'percentage') {
                orderDiscountAmount = totalAfterItemDiscounts * (parseFloat(orderToEdit.discount_value) / 100);
            } else if (orderToEdit.discount_type === 'fixed') {
                orderDiscountAmount = parseFloat(orderToEdit.discount_value);
            }
        }

        const deliveryFee = parseFloat(orderToEdit.delivery_fee) || 0;

        let couponDiscountAmount = 0;
        if (orderToEdit.coupon_discount_type === 'percentage') {
            couponDiscountAmount = totalAfterItemDiscounts * (parseFloat(orderToEdit.coupon_discount_value) / 100);
        } else if (orderToEdit.coupon_discount_type === 'fixed') {
            couponDiscountAmount = parseFloat(orderToEdit.coupon_discount_value);
        }

        const finalAmount = totalAfterItemDiscounts - orderDiscountAmount - couponDiscountAmount + deliveryFee;

        return {
            total_amount: subtotal,
            discount_amount: orderDiscountAmount,
            item_discount_amount: 0,
            order_discount_amount: orderDiscountAmount,
            coupon_discount_amount: couponDiscountAmount,
            final_amount: Math.max(0, finalAmount)
        };
    };

    const handleUpdateQuantity = (idx, delta) => {
        const newItems = [...editItems];
        const item = newItems[idx];
        const currentQty = Number(item.quantity) || 0;
        const newQty = currentQty + delta;

        if (newQty < 1) {
            handleRemoveItem(idx);
            return;
        }

        item.quantity = newQty;

        const productId = item.product_id;
        const groupItems = newItems.filter(i => i.product_id === productId);
        const totalQty = groupItems.reduce((sum, i) => sum + Number(i.quantity), 0);

        const offQty = Number(item.offer_qty) || 0;
        const offPrice = parseFloat(item.offer_price) || 0;

        groupItems.forEach(gItem => {
            let unitPrice = parseFloat(gItem.price) || 0;
            let unitDiscount = 0;

            if (offQty > 0 && offPrice > 0 && totalQty >= offQty) {
                const effectiveUnitPrice = offPrice / offQty;
                unitDiscount = unitPrice - effectiveUnitPrice;
            } else {
                if (gItem.discount_type === 'percentage') {
                    unitDiscount = unitPrice * (parseFloat(gItem.discount_value) / 100);
                } else if (gItem.discount_type === 'fixed') {
                    unitDiscount = parseFloat(gItem.discount_value || 0);
                }
            }

            gItem.discount = unitDiscount * gItem.quantity;
            gItem.total = (unitPrice * gItem.quantity) - gItem.discount;
        });

        setEditItems(newItems);
    };

    const handleRemoveItem = (idx) => {
        if (editItems.length <= 1) {
            alert("Order must have at least one item. Consider canceling the order instead.");
            return;
        }
        setEditItems(editItems.filter((_, i) => i !== idx));
    };

    // eslint-disable-next-line no-unused-vars
    const handleAddProduct = (product) => {
        // Simple add: check if already exists
        const exists = editItems.find(item => item.product_id === product.id && !item.variation_id);
        if (exists) {
            alert("Product already in order. Adjust quantity instead.");
            return;
        }

        const discountType = product.discount_is_active === 1 ? product.discount_type : null;
        const discountValue = product.discount_is_active === 1 ? product.discount_value : 0;

        const newItem = {
            product_id: product.id,
            product_name: product.name,
            product_photo: product.photo,
            quantity: 1,
            price: product.price,
            discount_type: discountType,
            discount_value: discountValue,
            offer_qty: product.offer_qty,
            offer_price: product.offer_price,
            discount: 0,
            total: 0,
            product_type: product.typeName || null
        };

        let unitPrice = parseFloat(newItem.price) || 0;
        let unitDiscount = 0;
        const offQty = Number(newItem.offer_qty) || 0;
        const offPrice = parseFloat(newItem.offer_price) || 0;

        if (offQty > 0 && offPrice > 0 && newItem.quantity >= offQty) {
            const effectiveUnitPrice = offPrice / offQty;
            unitDiscount = unitPrice - effectiveUnitPrice;
        } else {
            if (newItem.discount_type === 'percentage') {
                unitDiscount = unitPrice * (parseFloat(newItem.discount_value) / 100);
            } else if (newItem.discount_type === 'fixed') {
                unitDiscount = parseFloat(newItem.discount_value || 0);
            }
        }

        newItem.discount = unitDiscount * newItem.quantity;
        newItem.total = (unitPrice * newItem.quantity) - newItem.discount;

        setEditItems([...editItems, newItem]);
        setShowAddSection(false);
    };

    const handleSaveOrder = async () => {
        if (!orderToEdit) return;
        const newTotals = calculateTotals(editItems);

        try {
            setIsSaving(true);
            const response = await fetch(`${API_BASE_URL}/orders/${orderToEdit.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    items: editItems,
                    ...newTotals,
                    coupon_code: orderToEdit.coupon_code,
                    coupon_discount_value: orderToEdit.coupon_discount_value,
                    coupon_discount_type: orderToEdit.coupon_discount_type
                }),
            });
            if (!response.ok) throw new Error('Failed to update order');

            // Refetch orders to get clean state
            sessionStorage.removeItem('editingOrderId');
            sessionStorage.removeItem('editingOrderItems');
            setOrderToEdit(null);
            fetchOrders();
        } catch (err) {
            alert('Update failed: ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteOrder = (order) => {
        if (order.status?.toLowerCase() !== 'pending') {
            alert('Only pending orders can be canceled.');
            return;
        }
        setOrderToDelete(order);
    };

    const confirmDeleteOrder = async () => {
        if (!orderToDelete) return;
        try {
            const response = await fetch(`${API_BASE_URL}/orders/${orderToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });
            if (!response.ok) throw new Error('Failed to delete');
            setOrders(orders.filter(o => o.id !== orderToDelete.id));
            if (selectedOrder?.id === orderToDelete.id) setSelectedOrder(null);
            setOrderToDelete(null);
        } catch (err) {
            alert('Delete failed.');
            setOrderToDelete(null);
        }
    };

    const handleExchangeOrder = (order) => {
        setOrderToExchange(order);
        setExchangeNotes('');
        const items = order.items.flatMap(item => {
            // Expand items with quantity > 1 into separate rows
            const units = [];
            for (let i = 0; i < item.quantity; i++) {
                units.push({
                    ...item,
                    quantity: 1,  // Each row represents 1 unit
                    unitIndex: i,  // Track which unit this is
                    selected: false,
                    exchangeQty: 1,
                    old_product_id: item.product_id,
                    old_variation_id: item.variation_id
                });
            }
            return units;
        });
        setExchangeItems(items);
    };

    const toggleExchangeItemSelection = (idx) => {
        const items = [...exchangeItems];
        items[idx].selected = !items[idx].selected;
        setExchangeItems(items);
    };

    const sortSizes = (sizes) => {
        const order = ['xs', 's', 'm', 'l', 'xl', 'xxl', '3xl', '4xl'];
        return [...sizes].sort((a, b) => {
            const indexA = order.indexOf(a.toLowerCase());
            const indexB = order.indexOf(b.toLowerCase());
            if (indexA === -1 && indexB === -1) return a.localeCompare(b);
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    };

    const handleOpenQvExchange = async (item, idx) => {
        setQuickViewProduct(item);
        setQuickViewIndex(idx);

        try {
            const response = await fetch(`${API_BASE_URL}/admin/variations?prodID=${item.product_id}`);
            if (response.ok) {
                const data = await response.json();
                let vars = data.map(v => ({
                    ...v,
                    Color: v.ColorName || v.Color,
                    Size: v.SizeName || v.Size
                }));

                // Lock variations to the original color!
                const originalColor = item.variation_color;
                const sameColorVars = vars.filter(v => !originalColor || v.Color === originalColor);

                setQuickViewVariations(sameColorVars);
                setQvSelectedColor(originalColor);

                const sizesForColor = sameColorVars.map(v => v.Size).filter(Boolean);
                const sortedSizes = sortSizes([...new Set(sizesForColor)]);
                const hasCurrentSize = sortedSizes.includes(item.variation_size);
                setQvSelectedSize(hasCurrentSize ? item.variation_size : (sortedSizes.length > 0 ? sortedSizes[0] : null));
            }
        } catch (err) {
            console.error('Failed to fetch variations for exchange:', err);
        }
    };

    const handleConfirmSwap = () => {
        if (quickViewIndex === null) return;

        let matchedVar = null;
        if (quickViewVariations.length > 0) {
            matchedVar = quickViewVariations.find(v =>
                (!qvSelectedColor || v.Color === qvSelectedColor) &&
                (!qvSelectedSize || v.Size === qvSelectedSize)
            );
        }

        const items = [...exchangeItems];
        items[quickViewIndex] = {
            ...items[quickViewIndex],
            variation_id: matchedVar ? matchedVar.varID : null,
            variation_color: matchedVar ? matchedVar.Color : (qvSelectedColor || null),
            variation_size: matchedVar ? matchedVar.Size : (qvSelectedSize || null),
            variation_photo: matchedVar && matchedVar.photo ? matchedVar.photo : null,
            selected: true // Mark checked automatically
        };

        setExchangeItems(items);
        setQuickViewProduct(null);
        setQuickViewIndex(null);
        setQuickViewVariations([]);
    };

    const handleExchangeSubmit = async () => {
        if (!orderToExchange) return;

        const selectedItems = exchangeItems.filter(item => item.selected);
        if (selectedItems.length === 0) {
            alert('Please select at least one item to exchange.');
            return;
        }

        try {
            setIsExchanging(true);

            const exchangeFooters = selectedItems.map(item => ({
                original_order_footer_id: item.id,
                product_id: item.product_id,
                variation_id: item.variation_id,
                quantity: item.exchangeQty,
                price: parseFloat(item.price) || 0.00,
                discount: 0.00,
                total: 0.00,
                offer_id: item.offer_id || null,
                offer_qty: item.offer_qty || null,
                offer_price: item.offer_price || null,
                // Old item details (selected item before Swap Size)
                old_product_id: item.old_product_id || item.product_id,
                old_variation_id: item.old_variation_id || item.variation_id
            }));

            const total_amount = 0.00;
            const discount_amount = 0.00;
            const delivery_fee = parseFloat(orderToExchange.delivery_fee) || 0.00;
            const final_amount = delivery_fee;

            const payload = {
                original_order_id: orderToExchange.id,
                total_amount,
                discount_amount,
                delivery_fee,
                final_amount,
                status: 'pending',
                notes: exchangeNotes,
                items: exchangeFooters
            };

            const response = await fetch(`${API_BASE_URL}/orders/exchanges`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to submit exchange request');
            }

            setExchangeSuccessMessage(`Exchange request ${data.exchange_number} submitted successfully!`);
            setOrderToExchange(null);
            setExchangeItems([]);
            setExchangeNotes('');
            fetchOrders();
        } catch (err) {
            alert('Exchange failed: ' + err.message);
        } finally {
            setIsExchanging(false);
        }
    };

    // Icons
    const ShopIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
    );
    const CloseIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
    );
    const ChevronIcon = () => (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
    );

    if (loading || userLoading) {
        return (
            <div className="orders-container">
                <div className="orders-header">
                    <div style={{ height: '40px', width: '200px', borderRadius: '8px' }} className="skeleton"></div>
                </div>
                <div className="orders-list">
                    {[1, 2, 3].map(i => <div key={i} className="order-card skeleton" style={{ height: '60px' }}></div>)}
                </div>
            </div>
        );
    }

    if (error) return <div className="orders-container"><div className="no-orders-wrapper"><h2>Error</h2><p>{error}</p></div></div>;

    const filteredOrders = getFilteredOrders();

    return (
        <main className="orders-container">
            <header className={`orders-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="header-top">
                    <div className="title-group">
                        <h1>Orders</h1>
                        <Link to="/shop" className="shop-now-btn mobile-shop-btn"><ShopIcon /><span>Shop</span></Link>
                    </div>
                    <div className="header-actions">
                        <div className="filters-bar">
                            <div className="filter-group">
                                <label>From</label>
                                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="filter-input" />
                            </div>
                            <div className="filter-group">
                                <label>To</label>
                                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="filter-input" />
                            </div>
                            <div className="filter-group">
                                <label>Status</label>
                                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-input">
                                    <option value="">All Orders</option>
                                    <option value="pending">Pending</option>
                                    <option value="processing">Processing</option>
                                    <option value="shipped">Shipped</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                            {(fromDate || toDate || statusFilter) && (
                                <button className="reset-btn" onClick={() => { setFromDate(''); setToDate(''); setStatusFilter(''); }} title="Clear filters">
                                    <CloseIcon />
                                </button>
                            )}
                        </div>
                        <Link to="/shop" className="shop-now-btn desktop-shop-btn"><ShopIcon /><span>Shop</span></Link>
                    </div>
                </div>
            </header>

            {filteredOrders.length === 0 ? (
                <div className="no-orders-wrapper">
                    <h2>No orders</h2>
                    <p>You haven't placed any orders yet. Start exploring our collection!</p>
                    <Link to="/shop" className="shop-now-btn"><ShopIcon /><span>Explore</span></Link>
                </div>
            ) : (
                <div className="orders-list">
                    {filteredOrders.map((order) => (
                        <article key={order.id} className="order-card">
                            <div className="order-row-main">
                                <div className="order-main-info">
                                    <div className="info-group">
                                        <span className="label">Date</span>
                                        <span className="value">{formatDate(order.created_at)}</span>
                                    </div>
                                    <div className="info-group">
                                        <span className="label">Number</span>
                                        <span className="value">#{order.order_number}</span>
                                    </div>
                                    {order.original_order_number && (
                                        <div className="info-group">
                                            <span className="label">Original Order</span>
                                            <span className="value" style={{ fontWeight: '700', color: 'var(--primary)' }}>#{order.original_order_number}</span>
                                        </div>
                                    )}
                                    <div className="info-group">
                                        <span className="label">Total</span>
                                        <span className="value highlight">${parseFloat(order.final_amount).toFixed(2)}</span>
                                    </div>
                                </div>

                                <div className="order-main-status">
                                    <span className={`order-status ${getStatusClass(order.status)}`}>
                                        {order.order_number?.startsWith('EXC-')
                                            ? `Exchange (${order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending'})`
                                            : (order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : '')}
                                    </span>
                                </div>

                                <div className="order-main-actions">
                                    <button className="action-btn btn-primary" onClick={() => setSelectedOrder(order)}>
                                        Details <ChevronIcon />
                                    </button>
                                    {order.status?.toLowerCase() === 'confirmed' &&
                                        !order.order_number?.startsWith('EXC-') &&
                                        !orders.some(o => o.original_order_number === order.order_number) &&
                                        (new Date() - new Date(order.created_at)) / (1000 * 60 * 60) <= 24 && (
                                            <button className="action-btn btn-outline" onClick={() => handleExchangeOrder(order)}>Exchange</button>
                                        )}
                                    {order.status?.toLowerCase() === 'pending' && (
                                        <>
                                            {!order.order_number?.startsWith('EXC-') && (
                                                <button className="action-btn btn-outline" onClick={() => handleEditOrder(order)}>Edit</button>
                                            )}
                                            <button className="action-btn btn-danger" onClick={() => handleDeleteOrder(order)}>Cancel</button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {/* ORDER DETAILS MODAL */}
            {selectedOrder && (
                <div className="order-modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="order-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-mobile-handle"></div>
                        <div className="modal-header">
                            <div className="modal-title-group">
                                <h2>Order Summary</h2>
                                <p>
                                    Order #{selectedOrder.order_number} • {formatDate(selectedOrder.created_at)}
                                    {selectedOrder.original_order_number && (
                                        <span style={{ display: 'block', color: 'var(--primary)', fontWeight: 'bold', marginTop: '4px' }}>
                                            Original Order: #{selectedOrder.original_order_number}
                                        </span>
                                    )}
                                </p>
                            </div>
                            <button className="close-modal-btn" onClick={() => setSelectedOrder(null)}>
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="order-summary-box">
                                <div className="summary-item">
                                    <span className="summary-label">Order Status</span>
                                    <span className={`order-status ${getStatusClass(selectedOrder.status)}`}>
                                        {selectedOrder.order_number?.startsWith('EXC-')
                                            ? `Exchange (${selectedOrder.status ? selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1) : 'Pending'})`
                                            : (selectedOrder.status ? selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1) : '')}
                                    </span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">Items Count</span>
                                    <span className="summary-value" style={{ color: 'var(--text-main)' }}>{selectedOrder.items?.length || 0}</span>
                                </div>
                            </div>

                            <div className="items-section">
                                <div className="order-items-detailed-list">
                                    {selectedOrder.items?.map((item, idx) => {
                                        const groupItems = selectedOrder.items.filter(i => i.product_id === item.product_id);
                                        const totalQty = groupItems.reduce((sum, i) => sum + Number(i.quantity), 0);
                                        const isOfferApplied = item.offer_qty && item.offer_price && totalQty >= Number(item.offer_qty);

                                        return (
                                            <div key={idx} className="item-rich-row">
                                                <div className="item-visual">
                                                    {item.variation_photo || item.product_photo ? (
                                                        <img src={`${API_BASE_URL}${item.variation_photo || item.product_photo}`} alt={item.product_name} />
                                                    ) : (
                                                        <div className="item-img-placeholder-mini"><ShopIcon /></div>
                                                    )}
                                                </div>
                                                <div className="item-details">
                                                    <span className="item-name">{item.product_name}</span>
                                                    <span className="item-variant">
                                                        {item.variation_color} {item.variation_size}
                                                        {item.product_type && (
                                                            <>
                                                                <span style={{ margin: '0 6px', color: '#ccc' }}>•</span>
                                                                Fabric: <strong style={{ color: '#eb5e28', fontWeight: '600' }}>{item.product_type}</strong>
                                                            </>
                                                        )}
                                                    </span>
                                                    <div className="item-pricing-info">
                                                        <div className="price-stack">
                                                            {isOfferApplied || (!isOfferApplied && parseFloat(item.discount || 0) > 0) ? (
                                                                <span className="old-price">
                                                                    ${((parseFloat(item.total) + parseFloat(item.discount || 0)) / Number(item.quantity)).toFixed(2)}
                                                                </span>
                                                            ) : null}
                                                            <span className="current-price">${(parseFloat(item.total) / Number(item.quantity)).toFixed(2)}</span>
                                                            {parseFloat(item.discount_value) > 0 && !isOfferApplied && (
                                                                <span className="item-discount-tag">
                                                                    -{item.discount_type === 'percentage'
                                                                        ? `${Math.round(item.discount_value)}%`
                                                                        : `$${parseFloat(item.discount_value).toFixed(2)}`} off
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span className="item-quantity">Qty: {item.quantity}</span>
                                                            {item.offer_qty && item.offer_price && (
                                                                <span className="item-offer-tag">
                                                                    Offer: {item.offer_qty} for ${parseFloat(item.offer_price).toFixed(2)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="item-total-price">
                                                    ${parseFloat(item.total).toFixed(2)}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            <div className="order-breakdown-section">
                                <h3>Total Breakdown</h3>
                                <div className="breakdown-list">
                                    <div className="breakdown-row">
                                        <span>Subtotal</span>
                                        <span>${parseFloat(selectedOrder.total_amount).toFixed(2)}</span>
                                    </div>
                                    {(() => {
                                        const offerSavings = selectedOrder.items?.reduce((sum, item) => {
                                            const groupItems = selectedOrder.items.filter(i => i.product_id === item.product_id);
                                            const totalQty = groupItems.reduce((s, i) => s + Number(i.quantity), 0);
                                            if (item.offer_qty && item.offer_price && totalQty >= Number(item.offer_qty)) return sum + parseFloat(item.discount || 0);
                                            return sum;
                                        }, 0) || 0;
                                        if (offerSavings <= 0) return null;
                                        const offeredItems = selectedOrder.items?.filter(item => {
                                            const groupItems = selectedOrder.items.filter(i => i.product_id === item.product_id);
                                            const totalQty = groupItems.reduce((s, i) => s + Number(i.quantity), 0);
                                            return item.offer_qty && item.offer_price && totalQty >= Number(item.offer_qty);
                                        }) || [];
                                        return (
                                            <div className="breakdown-row offer">
                                                <span>Offers Applied ({offeredItems.length} item{offeredItems.length !== 1 ? 's' : ''})</span>
                                                <span style={{ color: '#10b981', fontWeight: '600' }}>-${offerSavings.toFixed(2)}</span>
                                            </div>
                                        );
                                    })()}
                                    {(() => {
                                        const itemSavings = selectedOrder.items?.reduce((sum, item) => {
                                            const groupItems = selectedOrder.items.filter(i => i.product_id === item.product_id);
                                            const totalQty = groupItems.reduce((s, i) => s + Number(i.quantity), 0);
                                            if (!(item.offer_qty && item.offer_price && totalQty >= Number(item.offer_qty)) && parseFloat(item.discount || 0) > 0) return sum + parseFloat(item.discount || 0);
                                            return sum;
                                        }, 0) || 0;
                                        if (itemSavings <= 0) return null;
                                        return (
                                            <div className="breakdown-row discount">
                                                <span>Item Discounts</span>
                                                <span>-${itemSavings.toFixed(2)}</span>
                                            </div>
                                        );
                                    })()}
                                    {(() => {
                                        const hasAppliedOffers = selectedOrder.items?.some(item => {
                                            const groupItems = selectedOrder.items.filter(i => i.product_id === item.product_id);
                                            const totalQty = groupItems.reduce((sum, i) => sum + Number(i.quantity), 0);
                                            return item.offer_qty && item.offer_price && totalQty >= Number(item.offer_qty);
                                        });
                                        const hasItemDiscounts = selectedOrder.items?.some(item => parseFloat(item.discount_value || 0) > 0);
                                        const orderLevelDiscount = parseFloat(selectedOrder.discount_amount || 0);
                                        if (orderLevelDiscount <= 0 || hasAppliedOffers || hasItemDiscounts) return null;
                                        return (
                                            <div className="breakdown-row discount">
                                                <span>
                                                    Order Discount {parseFloat(selectedOrder.discount_value) > 0 ? `(${selectedOrder.discount_type === 'percentage' ? `${Math.round(selectedOrder.discount_value)}%` : `$${parseFloat(selectedOrder.discount_value).toFixed(2)}`})` : ''}
                                                </span>
                                                <span>-${orderLevelDiscount.toFixed(2)}</span>
                                            </div>
                                        );
                                    })()}
                                    {parseFloat(selectedOrder.coupon_discount_value || 0) > 0 && (
                                        <div className="breakdown-row discount">
                                            <span>
                                                Coupon: {selectedOrder.coupon_code} ({selectedOrder.coupon_discount_type === 'percentage' ? `${Math.round(selectedOrder.coupon_discount_value)}%` : `$${parseFloat(selectedOrder.coupon_discount_value).toFixed(2)}`})
                                            </span>
                                            <span>
                                                -${(selectedOrder.coupon_discount_type === 'percentage'
                                                    ? (parseFloat(selectedOrder.total_amount) * parseFloat(selectedOrder.coupon_discount_value) / 100)
                                                    : parseFloat(selectedOrder.coupon_discount_value)).toFixed(2)}
                                            </span>
                                        </div>
                                    )}
                                    <div className="breakdown-row">
                                        <span>Delivery Fee</span>
                                        {parseFloat(selectedOrder.delivery_fee) === 0 ? (
                                            <span style={{ color: '#10b981', fontWeight: '700' }}>Free Delivery Earned</span>
                                        ) : (
                                            <span>${parseFloat(selectedOrder.delivery_fee).toFixed(2)}</span>
                                        )}
                                    </div>
                                    <div className="breakdown-row grand-total">
                                        <span>Grand Total</span>
                                        <span>${parseFloat(selectedOrder.final_amount).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* EDIT ORDER MODAL */}
            {orderToEdit && (
                <div className="order-modal-overlay" onClick={() => setOrderToEdit(null)}>
                    <div className="order-modal-content edit-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-title-group">
                                <h2>Edit Order Summary</h2>
                                <p>Order #{orderToEdit.order_number} • {formatDate(orderToEdit.created_at)}</p>
                            </div>
                            <button className="close-modal-btn" onClick={() => {
                                setOrderToEdit(null);
                                sessionStorage.removeItem('editingOrderId');
                                sessionStorage.removeItem('editingOrderItems');
                            }}>
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="modal-body edit-body">
                            <div className="order-summary-box">
                                <div className="summary-item">
                                    <span className="summary-label">Order Status</span>
                                    <span className={`order-status ${getStatusClass(orderToEdit.status)}`}>{orderToEdit.status}</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">Items Count</span>
                                    <span className="summary-value" style={{ color: 'var(--text-main)' }}>{editItems.length}</span>
                                </div>
                                <button className="add-item-trigger" style={{ marginLeft: 'auto', alignSelf: 'center' }} onClick={() => {
                                    sessionStorage.setItem('editingOrderId', orderToEdit.id);
                                    sessionStorage.setItem('editingOrderItems', JSON.stringify(editItems));
                                    navigate('/shop');
                                }}>
                                    + Add Item
                                </button>
                            </div>

                            <div className="items-section edit-items-section" style={{ marginTop: '1rem' }}>

                                <div className="edit-items-list">
                                    {editItems.map((item, idx) => {
                                        const groupItems = editItems.filter(i => i.product_id === item.product_id);
                                        const totalQty = groupItems.reduce((sum, i) => sum + Number(i.quantity), 0);
                                        const isOfferApplied = item.offer_qty && item.offer_price && totalQty >= Number(item.offer_qty);

                                        return (
                                            <div key={idx} className="item-edit-row">
                                                <div className="item-mini-visual">
                                                    <img src={`${API_BASE_URL}${item.variation_photo || item.product_photo}`} alt="" />
                                                </div>
                                                <div className="item-edit-info">
                                                    <span className="item-name">{item.product_name}</span>
                                                    <span className="item-variant">
                                                        {item.variation_color} {item.variation_size}
                                                        {item.product_type && (
                                                            <>
                                                                <span style={{ margin: '0 6px', color: '#ccc' }}>•</span>
                                                                Fabric: <strong style={{ color: '#eb5e28', fontWeight: '600' }}>{item.product_type}</strong>
                                                            </>
                                                        )}
                                                    </span>
                                                    <div className="item-pricing-info">
                                                        <div className="price-stack" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            {isOfferApplied || (!isOfferApplied && parseFloat(item.discount_value) > 0) ? (
                                                                <span className="old-price">
                                                                    ${parseFloat(item.price).toFixed(2)}
                                                                </span>
                                                            ) : null}
                                                            <span className="current-price">
                                                                ${(Number(item.total) / Number(item.quantity)).toFixed(2)}
                                                            </span>
                                                        </div>
                                                        {parseFloat(item.discount_value) > 0 && !isOfferApplied && (
                                                            <span className="item-discount-tag">
                                                                -{item.discount_type === 'percentage'
                                                                    ? `${Math.round(item.discount_value)}%`
                                                                    : `$${parseFloat(item.discount_value).toFixed(2)}`} off
                                                            </span>
                                                        )}
                                                        {item.offer_qty && item.offer_price && (
                                                            <span className="item-offer-tag mobile-offer-tag">
                                                                Offer: {item.offer_qty} for ${parseFloat(item.offer_price).toFixed(2)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="item-qty-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                                    <div className="item-qty-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <button onClick={() => handleUpdateQuantity(idx, -1)}>−</button>
                                                        <span>{item.quantity}</span>
                                                        <button onClick={() => handleUpdateQuantity(idx, 1)}>+</button>
                                                    </div>
                                                    {item.offer_qty && item.offer_price && (
                                                        <span className="item-offer-tag desktop-offer-tag">
                                                            Offer: {item.offer_qty} for ${parseFloat(item.offer_price).toFixed(2)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            <div className="order-breakdown-section">
                                <h3>Total Breakdown Preview</h3>
                                <div className="breakdown-list">
                                    <div className="breakdown-row">
                                        <span>Subtotal</span>
                                        <span>${calculateTotals(editItems).total_amount.toFixed(2)}</span>
                                    </div>
                                    {(() => {
                                        const totals = calculateTotals(editItems);
                                        const hasItemDiscounts = totals.item_discount_amount > 0;
                                        const offeredItems = editItems?.filter(item => {
                                            const groupItems = editItems.filter(i => i.product_id === item.product_id);
                                            const totalQty = groupItems.reduce((sum, i) => sum + Number(i.quantity), 0);
                                            const offQty = Number(item.offer_qty) || 0;
                                            return offQty > 0 && totalQty >= offQty;
                                        }) || [];
                                        return (
                                            <>
                                                {offeredItems.length > 0 && (
                                                    <div className="breakdown-row offer">
                                                        <span>Offers Applied ({offeredItems.length} item{offeredItems.length !== 1 ? 's' : ''})</span>
                                                        <span style={{ color: '#10b981', fontWeight: '600' }}>Applied in Subtotal</span>
                                                    </div>
                                                )}
                                                {hasItemDiscounts && (
                                                    <div className="breakdown-row discount">
                                                        <span>Item Discounts</span>
                                                        <span>-${totals.item_discount_amount.toFixed(2)}</span>
                                                    </div>
                                                )}
                                                {totals.order_discount_amount > 0 && offeredItems.length === 0 && !hasItemDiscounts && (
                                                    <div className="breakdown-row discount">
                                                        <span>
                                                            Order Discount {parseFloat(orderToEdit.discount_value) > 0 ? `(${orderToEdit.discount_type === 'percentage' ? `${Math.round(orderToEdit.discount_value)}%` : `$${parseFloat(orderToEdit.discount_value).toFixed(2)}`})` : ''}
                                                        </span>
                                                        <span>-${totals.order_discount_amount.toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                    {calculateTotals(editItems).coupon_discount_amount > 0 && (
                                        <div className="breakdown-row discount">
                                            <span>
                                                Coupon: {orderToEdit.coupon_code} ({orderToEdit.coupon_discount_type === 'percentage' ? `${Math.round(orderToEdit.coupon_discount_value)}%` : `$${parseFloat(orderToEdit.coupon_discount_value).toFixed(2)}`})
                                            </span>
                                            <span>-${calculateTotals(editItems).coupon_discount_amount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="breakdown-row">
                                        <span>Delivery Fee</span>
                                        {parseFloat(orderToEdit.delivery_fee) === 0 ? (
                                            <span style={{ color: '#10b981', fontWeight: '700' }}>Free Delivery Earned</span>
                                        ) : (
                                            <span>${parseFloat(orderToEdit.delivery_fee).toFixed(2)}</span>
                                        )}
                                    </div>
                                    <div className="breakdown-row grand-total">
                                        <span>New Grand Total</span>
                                        <span>${calculateTotals(editItems).final_amount.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer edit-footer">
                            <button className="action-btn btn-outline" onClick={() => setOrderToEdit(null)}>Cancel</button>
                            <button
                                className="action-btn btn-primary"
                                onClick={handleSaveOrder}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* EXCHANGE ORDER MODAL */}
            {orderToExchange && (
                <div className="order-modal-overlay" onClick={() => { setOrderToExchange(null); setExchangeItems([]); setExchangeNotes(''); }}>
                    <div className="order-modal-content edit-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-title-group">
                                <h2>Request Order Exchange</h2>
                                <p>Order #{orderToExchange.order_number} • {formatDate(orderToExchange.created_at)}</p>
                            </div>
                            <button className="close-modal-btn" onClick={() => { setOrderToExchange(null); setExchangeItems([]); setExchangeNotes(''); }}>
                                <CloseIcon />
                            </button>
                        </div>

                        <div className="modal-body edit-body">
                            <div className="order-summary-box" style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                                    <strong>How it works:</strong> Check the items you want to exchange, click <strong>"Swap Size"</strong> to select your preferred replacement size, and click <strong>"Submit Exchange Request"</strong> below.
                                </p>
                            </div>

                            <div className="items-section edit-items-section" style={{ padding: 0, background: 'transparent' }}>
                                <h3 style={{ fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '1px', marginBottom: '12px' }}>Your Purchased Items</h3>
                                <div className="edit-items-list">
                                    {exchangeItems.map((item, idx) => {
                                        return (
                                            <div key={idx} className="item-edit-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', border: item.selected ? '1.5px solid var(--primary)' : '1px solid var(--border)', background: item.selected ? 'rgba(255, 71, 87, 0.02)' : 'white' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                                    <div className="item-checkbox-container" style={{ display: 'flex', alignItems: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            id={`exchange-chk-${idx}`}
                                                            checked={item.selected || false}
                                                            onChange={() => toggleExchangeItemSelection(idx)}
                                                            style={{
                                                                width: '18px',
                                                                height: '18px',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                accentColor: 'var(--primary)'
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="item-mini-visual">
                                                        <img src={`${API_BASE_URL}${item.variation_photo || item.product_photo}`} alt="" />
                                                    </div>
                                                    <div className="item-edit-info" style={{ minWidth: 0 }}>
                                                        <span className="item-name" style={{ fontWeight: '700', fontSize: '0.85rem' }}>{item.product_name}</span>
                                                        <span className="item-variant" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                            {item.variation_color} {item.variation_size}
                                                            {item.product_type && (
                                                                <>
                                                                    <span style={{ margin: '0 6px', color: '#ccc' }}>•</span>
                                                                    Fabric: <strong style={{ color: '#eb5e28', fontWeight: '600' }}>{item.product_type}</strong>
                                                                </>
                                                            )}
                                                        </span>
                                                        <div className="item-pricing-info" style={{ marginTop: '4px' }}>
                                                            <span className="item-quantity" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                                Unit {(item.unitIndex || 0) + 1} • Qty: {item.quantity}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="item-qty-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <button
                                                        className="action-btn btn-primary"
                                                        style={{
                                                            padding: '8px 16px',
                                                            fontSize: '0.75rem',
                                                            borderRadius: '8px',
                                                            textTransform: 'uppercase',
                                                            height: 'auto',
                                                            background: 'var(--primary)',
                                                            borderColor: 'var(--primary)',
                                                            color: 'white',
                                                            fontWeight: '700',
                                                            minWidth: '130px',
                                                            textAlign: 'center'
                                                        }}
                                                        onClick={() => handleOpenQvExchange(item, idx)}
                                                    >
                                                        Swap Size
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            <div className="exchange-notes-section" style={{ marginTop: '1.5rem' }}>
                                <h3 style={{ marginBottom: '8px', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '1px' }}>Reason for Exchange / Additional Notes</h3>
                                <textarea
                                    value={exchangeNotes}
                                    onChange={e => setExchangeNotes(e.target.value)}
                                    style={{
                                        width: '100%',
                                        minHeight: '44px',
                                        padding: '10px 14px',
                                        borderRadius: '16px',
                                        border: '1.5px solid var(--border)',
                                        fontFamily: 'inherit',
                                        fontSize: '0.85rem',
                                        resize: 'none',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        background: 'var(--surface)',
                                        color: 'var(--text-main)',
                                        transition: 'all 0.2s'
                                    }}
                                    onFocus={e => e.currentTarget.style.borderColor = 'var(--primary)'}
                                    onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                                />
                            </div>

                            {/* Exchange Delivery Charge */}
                            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '8px', padding: '0 4px', fontSize: '0.85rem', fontWeight: '700', color: 'var(--text-main)' }}>
                                <span>Exchange Delivery Charge:</span>
                                <span style={{ fontWeight: '800', color: 'var(--primary)' }}>
                                    ${(parseFloat(orderToExchange.delivery_fee) || 0).toFixed(2)}
                                </span>
                            </div>
                        </div>

                        <div className="modal-footer edit-footer" style={{ gap: '12px' }}>
                            <button className="action-btn btn-outline" onClick={() => { setOrderToExchange(null); setExchangeItems([]); setExchangeNotes(''); }}>Cancel</button>
                            <button
                                className="action-btn btn-primary"
                                disabled={isExchanging || !exchangeItems.some(i => i.selected)}
                                onClick={handleExchangeSubmit}
                                style={{
                                    background: exchangeItems.some(i => i.selected) ? 'var(--primary)' : 'var(--text-muted)',
                                    borderColor: exchangeItems.some(i => i.selected) ? 'var(--primary)' : 'var(--border)',
                                    color: 'white',
                                    cursor: exchangeItems.some(i => i.selected) ? 'pointer' : 'not-allowed'
                                }}
                            >
                                {isExchanging ? 'Submitting...' : 'Submit Exchange Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* LOCAL QUICK VIEW DIALOG FOR SIZE/COLOR SWAP */}
            {quickViewProduct && (
                <div
                    className="qv-overlay"
                    onClick={() => { setQuickViewProduct(null); setQuickViewVariations([]); }}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Product quick view"
                    style={{ zIndex: 1100 }}
                >
                    <div
                        className="qv-panel"
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            className="qv-close"
                            onClick={() => { setQuickViewProduct(null); setQuickViewVariations([]); }}
                            aria-label="Close"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="1" y1="1" x2="19" y2="19" /><line x1="19" y1="1" x2="1" y2="19" />
                            </svg>
                        </button>

                        <div className="qv-body">
                            {/* image column */}
                            <div className="qv-image-col">
                                <div className="qv-image-frame">
                                    {(() => {
                                        const displayPhoto = (quickViewVariations.find(v => v.Color === qvSelectedColor && v.Size === qvSelectedSize)?.photo) ||
                                            (quickViewVariations.find(v => v.Color === qvSelectedColor)?.photo) ||
                                            quickViewProduct.variation_photo ||
                                            quickViewProduct.product_photo;

                                        return displayPhoto ? (
                                            <img
                                                src={`${API_BASE_URL}${displayPhoto}`}
                                                alt={quickViewProduct.product_name}
                                                className="qv-image"
                                            />
                                        ) : (
                                            <div className="qv-image product-placeholder">
                                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.25">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
                                                    <polyline points="21 15 16 10 5 21" />
                                                </svg>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* details column */}
                            <div className="qv-details">
                                <span className="qv-category">Order Exchange Size</span>
                                <h2 className="qv-title">{quickViewProduct.product_name}</h2>
                                <p className="qv-desc" style={{ marginBottom: '20px' }}>
                                    Select your preferred replacement size below to update your exchange request.
                                </p>

                                <div className="qv-price-row" style={{ marginBottom: '20px', gap: '8px', display: 'flex', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Original:</span>
                                    <span className="qv-sale-tag" style={{ background: 'var(--surface)', color: 'var(--text-main)', border: '1px solid var(--border)' }}>
                                        {quickViewProduct.variation_color} {quickViewProduct.variation_size}
                                    </span>
                                </div>

                                <div className="qv-variations">
                                    {/* Sizes */}
                                    {quickViewVariations.length > 0 && (
                                        (() => {
                                            const availableSizes = sortSizes([...new Set(quickViewVariations.map(v => v.Size).filter(Boolean))]);
                                            if (availableSizes.length === 0) return null;

                                            return (
                                                <div className="qv-var-group">
                                                    <span className="qv-var-label">Available Sizes in Color <strong>{qvSelectedColor}</strong></span>
                                                    <div className="qv-var-options">
                                                        {availableSizes.map(size => {
                                                            const matchedVar = quickViewVariations.find(v => v.Size === size);
                                                            const outOfStock = matchedVar ? parseInt(matchedVar.Qty || 0) === 0 : true;

                                                            return (
                                                                <button
                                                                    key={size}
                                                                    className={`qv-var-btn ${qvSelectedSize === size ? 'active' : ''} ${outOfStock ? 'out-of-stock' : ''}`}
                                                                    onClick={() => setQvSelectedSize(size)}
                                                                >
                                                                    {size}
                                                                    {outOfStock && <span className="qv-var-x" />}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })()
                                    )}
                                </div>

                                <div className="qv-actions" style={{ marginTop: '30px', display: 'flex', gap: '12px' }}>
                                    <button
                                        className="btn-outline btn-large"
                                        onClick={() => { setQuickViewProduct(null); setQuickViewVariations([]); }}
                                        style={{ flex: 1, padding: '14px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: '700' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn-primary btn-large"
                                        onClick={handleConfirmSwap}
                                        style={{ flex: 1, padding: '14px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: '700' }}
                                    >
                                        Confirm Swap
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRMATION MODAL */}
            {orderToDelete && (
                <div className="order-modal-overlay" onClick={() => setOrderToDelete(null)}>
                    <div className="confirmation-modal" onClick={e => e.stopPropagation()}>
                        <div className="confirmation-content">
                            <div className="warning-icon">
                                <CloseIcon />
                            </div>
                            <h3>Delete Order #{orderToDelete.order_number}?</h3>
                            <p>This action cannot be undone. Are you sure you want to cancel this order?</p>
                            <div className="confirmation-actions">
                                <button className="confirm-btn action-btn btn-danger" onClick={confirmDeleteOrder}>Delete Order</button>
                                <button className="cancel-btn action-btn btn-outline" onClick={() => setOrderToDelete(null)}>Go Back</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* EXCHANGE SUCCESS DIALOG */}
            {exchangeSuccessMessage && (
                <div className="order-modal-overlay" onClick={() => setExchangeSuccessMessage('')}>
                    <div className="confirmation-modal" onClick={e => e.stopPropagation()}>
                        <div className="confirmation-content">
                            <div className="success-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', background: '#10b981', borderRadius: '50%', margin: '0 auto 16px', color: 'white' }}>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                            </div>
                            <h3 style={{ color: '#10b981', marginBottom: '8px' }}>{exchangeSuccessMessage}</h3>
                            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginBottom: '20px' }}>Your exchange request has been received and will be processed shortly.</p>
                            <div className="confirmation-actions">
                                <button className="action-btn btn-primary" onClick={() => setExchangeSuccessMessage()} style={{ width: '100%' }}>Done</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default Orders;
